import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Building, Mail, Lock, Eye, EyeOff, AlertCircle, User, Hash, MapPin, Star, Loader2, Languages } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { authAPI } from "@/api";
import "../LoginFixes.css";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { isNativePlatform } from "@/utils/platform";
import type { LocationValue, PersonCategory } from '@/types';
import { CATEGORY_OPTIONS } from '@/types';
import LocationSelector from '@/components/LocationSelector';
import { syncNotificationState } from '@/utils/notification-sync';
import Modal from "@/components/Modal";
import LanguageSelector from "@/components/LanguageSelector";
import { SUPPORTED_LANGUAGES } from "@/i18n/config";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    loading,
    refreshUser,
    updateRequired,
    updateLink,
    requiredAppVersion,
    deviceAppVersion,
    checkVersionGate,
  } = useAuth();
  const { t, i18n } = useTranslation();

  // Add classes to body and html for scroll fixes
  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    const rootElement = document.getElementById('root');
    
    // Add classes for scroll fixes
    htmlElement.classList.add('login-page-html');
    bodyElement.classList.add('login-page-body');
    
    // Force override with direct styles as backup
    const originalBodyStyle = bodyElement.style.cssText;
    const originalHtmlStyle = htmlElement.style.cssText;
    const originalRootStyle = rootElement ? rootElement.style.cssText : '';
    
    bodyElement.style.cssText += '; overflow: auto !important; height: auto !important; position: static !important;';
    htmlElement.style.cssText += '; overflow: auto !important; height: auto !important; position: static !important;';
    if (rootElement) {
      rootElement.style.cssText += '; overflow: auto !important; height: auto !important; position: static !important;';
    }
    
    // Cleanup on unmount
    return () => {
      htmlElement.classList.remove('login-page-html');
      bodyElement.classList.remove('login-page-body');
      
      // Restore original styles
      bodyElement.style.cssText = originalBodyStyle;
      htmlElement.style.cssText = originalHtmlStyle;
      if (rootElement) {
        rootElement.style.cssText = originalRootStyle;
      }
    };
  }, []);

  // Get current language display name
  const getCurrentLanguageLabel = () => {
    const currentLang = SUPPORTED_LANGUAGES.find(lang => lang.code === i18n.language);
    return currentLang ? currentLang.nativeName : i18n.language.toUpperCase();
  };

  // Main state variables
  const [activeTab, setActiveTab] = useState("login");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Register form state
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [location, setLocation] = useState<LocationValue>({ country: null, city: null });
  const [selectedRole, setSelectedRole] = useState<PersonCategory | undefined>(undefined);
  const [registerError, setRegisterError] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Field validation errors
  const [fieldErrors, setFieldErrors] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    location: "",
    role: ""
  });

  // Password Reset State
  const [isEnteringOtp, setIsEnteringOtp] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // Registration OTP State
  const [isEnteringRegisterOtp, setIsEnteringRegisterOtp] = useState(false);
  const [registerOtpEmail, setRegisterOtpEmail] = useState("");
  const [registerOtpCode, setRegisterOtpCode] = useState("");
  const [registerOtpError, setRegisterOtpError] = useState("");
  const [isVerifyingRegisterOtp, setIsVerifyingRegisterOtp] = useState(false);

  // Redirect authenticated users directly into the app
  useEffect(() => {
    if (isAuthenticated && !loading && !updateRequired) {
      // Small delay to ensure all auth state is settled before navigation
      const navigationTimer = setTimeout(() => {
        
        navigate('/', { replace: true });
      }, 100);
      
      return () => clearTimeout(navigationTimer);
    }
  }, [isAuthenticated, loading, updateRequired, navigate]);

  // Reset field errors when main auth flows change
  useEffect(() => {
    setFieldErrors({
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      location: "",
      role: ""
    });
  }, [activeTab, isEnteringOtp, isEnteringRegisterOtp]);

  // Reset transient submit states when operations fail
  useEffect(() => {
    if ((isSubmitting && !isLoading && (loginError || registerError)) || 
        (isSendingOtp && !isLoading && otpError)) {
      setIsSubmitting(false);
      setIsSendingOtp(false);
    }
  }, [isLoading, isSubmitting, isSendingOtp, loginError, registerError, otpError]);


  // Show loading if auth is still loading
  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-25">
        <div className="space-y-4 text-center">
          <div className="h-12 w-12 mx-auto border-[3px] border-brand-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Validation functions
  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePassword = (password: string) => {
    return password.length >= 8;
  };

  const validateOtp = (otp: string) => {
    return /^\d{6}$/.test(otp);
  };

  const validateFullName = (name: string): string => {
    if (!name.trim()) {
      return t('validation.full_name_required');
    }
    if (name.trim().length < 2) {
      return t('validation.full_name_min_length');
    }
    const nameRegex = /^[a-zA-ZçöüığşÇÖÜIĞŞ\s\-\.]+$/;
    if (!nameRegex.test(name)) {
      return t('validation.full_name_invalid_chars');
    }
    const words = name.trim().split(/\s+/);
    if (words.length < 2) {
      return t('validation.full_name_need_both');
    }
    return "";
  };

  const validateEmailField = (email: string): string => {
    if (!email.trim()) {
      return t('validation.email_required');
    }
    if (!validateEmail(email)) {
      return t('auth.invalid_email');
    }
    return "";
  };

  const validatePasswordField = (password: string): string => {
    if (!password) {
      return t('validation.password_required');
    }
    if (password.length < 8) {
      return t('auth.password_min_length');
    }
    return "";
  };

  const validateConfirmPasswordField = (password: string, confirmPassword: string): string => {
    if (!confirmPassword) {
      return t('validation.confirm_password_required');
    }
    if (password !== confirmPassword) {
      return t('auth.passwords_dont_match');
    }
    return "";
  };

  const validateLocationField = (location: LocationValue): string => {
    if (!location.country) {
      return t('validation.country_required');
    }
    if (!location.city) {
      return t('validation.city_required');
    }
    return "";
  };

  const validateRoleField = (role: PersonCategory | undefined): string => {
    if (!role) {
      return t('validation.role_required');
    }
    return "";
  };

  const validateField = (fieldName: string, value: any) => {
    let error = "";
    
    switch (fieldName) {
      case 'fullName':
        error = validateFullName(value);
        break;
      case 'email':
        error = validateEmailField(value);
        break;
      case 'password':
        error = validatePasswordField(value);
        break;
      case 'confirmPassword':
        error = validateConfirmPasswordField(registerPassword, value);
        break;
      case 'location':
        error = validateLocationField(value);
        break;
      case 'role':
        error = validateRoleField(value);
        break;
    }
    
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  const normalizeAuthError = (message?: string) => {
    if (!message) return t('auth.invalid_credentials');
    const lower = message.toLowerCase();
    if (lower.includes('captcha_token') || lower.includes('captcha protection')) {
      return "Supabase Auth still has CAPTCHA protection enabled. Disable it in Dashboard > Auth > Bot and Abuse Protection, or re-enable captcha in app.";
    }
    return message;
  };

  // Login handlers
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoginError("");
    setOtpError("");
    setIsEnteringOtp(false);
    
    if (!validateEmail(loginEmail)) {
      setLoginError(t('auth.invalid_email'));
      return;
    }
    
    if (!validatePassword(loginPassword)) {
      setLoginError(t('auth.password_min_length'));
      return;
    }
    
    setIsSubmitting(true);
    setIsLoading(true);
    completeLogin();
  };

  const completeLogin = async () => {
    setLoginError("");
    setIsLoading(true);
    
    try {
      const { data, error } = await authAPI.signIn(loginEmail, loginPassword);
      
      if (error) {
        setLoginError(normalizeAuthError(error.message));
        setIsSubmitting(false);
        setIsLoading(false);
        return;
      }
      
      if (data && data.session) {
        const canEnter = await checkVersionGate();
        if (!canEnter) {
          await authAPI.signOut();
          setLoginError("Uygulamayi kullanmaya devam etmek icin guncelleme yapmaniz gerekiyor.");
          setIsLoading(false);
          setIsSubmitting(false);
          return;
        }

        // Set session expiry
        const expiryTime = Math.floor(Date.now() / 1000) + 86400; // 24 hours
        localStorage.setItem('santiyex-session-expiry', expiryTime.toString());
        
        // Update OneSignal in background
        if (isNativePlatform()) {
                      setTimeout(async () => {
              try {
                await syncNotificationState(data.session.user.id);
                const { updateMissingOneSignalIds } = await import('@/utils/onesignal-migration');
                await updateMissingOneSignalIds();
              } catch (syncError) {
                
              }
            }, 1500);
        }
        
        // CRITICAL FIX: Trigger the same subscription checking flow as app restart
        
        
        // First refresh user data
        const userRefreshSuccess = await refreshUser();
        
        // Reset loading states
        setIsLoading(false);
        setIsSubmitting(false);
        
        if (userRefreshSuccess) {
          
          
          // IMPORTANT: Set a flag to indicate that subscription should be refreshed
          // This is picked up by the subscription context listener.
          localStorage.setItem('santiyex-force-subscription-refresh', 'true');
          
          // Also trigger a manual subscription refresh after a short delay to ensure auth is settled
          setTimeout(() => {
            
            // Dispatch a custom event that the subscription context can listen to
            window.dispatchEvent(new CustomEvent('santiyex-force-subscription-refresh'));
          }, 500);
          
        } else {
          
        }
        
      } else {
        setLoginError("Giriş sırasında bir hata oluştu.");
        setIsLoading(false);
        setIsSubmitting(false);
      }
    } catch (error: any) {
      setLoginError("Bir hata oluştu. Lütfen daha sonra tekrar deneyin.");
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  // Registration handlers
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setRegisterError("");
    
    // Validate all fields
    const fullNameError = validateFullName(fullName);
    const emailError = validateEmailField(registerEmail);
    const passwordError = validatePasswordField(registerPassword);
    const confirmPasswordError = validateConfirmPasswordField(registerPassword, confirmPassword);
    const locationError = validateLocationField(location);
    const roleError = validateRoleField(selectedRole);
    
    if (fullNameError || emailError || passwordError || confirmPasswordError || locationError || roleError) {
      setFieldErrors({
        fullName: fullNameError,
        email: emailError,
        password: passwordError,
        confirmPassword: confirmPasswordError,
        location: locationError,
        role: roleError
      });
      return;
    }
    
    setIsSubmitting(true);
    setIsLoading(true);
    completeRegistration();
  };

  const completeRegistration = async () => {
    setIsLoading(true);
    
    try {
      const { data, error } = await authAPI.signUp(
        registerEmail,
        registerPassword,
        {
          name: fullName,
          company: companyName,
          country: location.country?.label || null,
          city: location.city?.label || null,
          role: selectedRole,
        }
      );

      if (error) {
        setRegisterError(normalizeAuthError(error.message));
        setIsLoading(false);
        setIsSubmitting(false);
        return;
      }

      if (data && data.user) {
        toast({
          title: "Kayıt Başarılı",
          description: `Doğrulama kodu ${registerEmail} adresine gönderildi.`,
        });

        setRegisterOtpEmail(registerEmail);
        setIsEnteringRegisterOtp(true);
        setActiveTab("register");

        // Clear form fields
        setRegisterPassword("");
        setConfirmPassword("");
        setFullName("");
        setCompanyName("");
        setRegisterError("");
        setLocation({ country: null, city: null });
        setSelectedRole(undefined);
      } else {
        setRegisterError("Kayıt sırasında bir hata oluştu.");
      }
    } catch (error: any) {
      setRegisterError("Kayıt sırasında beklenmeyen bir hata oluştu.");
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  // Password reset handlers
  const handleForgotPassword = async () => {
    if (!loginEmail || !validateEmail(loginEmail)) {
      setOtpError("Lütfen geçerli bir e-posta adresi giriniz.");
      return;
    }
    
    setResetEmail(loginEmail);
    setIsSendingOtp(true);
    setIsLoading(true);
    completeForgotPassword();
  };

  const completeForgotPassword = async () => {
    try {
      const { success } = await authAPI.resetPassword(resetEmail);
      
      if (!success) {
        setOtpError("Şifre sıfırlama başarısız oldu.");
        setIsSendingOtp(false);
        setIsLoading(false);
        return;
      }
      
      toast({
        title: "Doğrulama Kodu Gönderildi",
        description: `Şifre sıfırlama kodu ${resetEmail} adresine gönderildi.`,
      });
      
      setIsEnteringOtp(true);
      setIsSendingOtp(false);
      setIsLoading(false);
      setOtpError("");
      
    } catch (error) {
      setOtpError("Şifre sıfırlama sırasında bir hata oluştu.");
      setIsSendingOtp(false);
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError("");

    if (!validateOtp(otpCode)) {
      setOtpError("Lütfen 6 haneli doğrulama kodunu giriniz.");
      return;
    }

    if (!newPassword || newPassword.length < 8) {
      setOtpError("Yeni şifre en az 8 karakter olmalıdır.");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setOtpError("Şifreler eşleşmiyor.");
      return;
    }

    setIsVerifyingOtp(true);
    setIsLoading(true);

    try {
      const verifyResponse = await authAPI.verifyOtp({
        email: resetEmail,
        token: otpCode,
        type: 'recovery'
      });

      if (!verifyResponse || verifyResponse.error) {
        setOtpError(verifyResponse?.error?.message || "Geçersiz veya süresi dolmuş doğrulama kodu.");
        setIsVerifyingOtp(false);
        setIsLoading(false);
        return;
      }

      const updateResponse = await authAPI.updateUserPassword(newPassword);

      if (!updateResponse || updateResponse.error) {
        setOtpError(updateResponse?.error?.message || "Şifre güncellenemedi. Lütfen tekrar deneyin.");
        setIsVerifyingOtp(false);
        setIsLoading(false);
        return;
      }

      toast({
        title: "Şifre Başarıyla Güncellendi",
        description: "Giriş sayfasına yönlendiriliyorsunuz.",
        variant: "default",
      });

      // Reset state and navigate
      setIsEnteringOtp(false);
      setOtpCode("");
      setNewPassword("");
      setConfirmNewPassword("");
      setOtpError("");
      setResetEmail("");
      setLoginEmail("");
      setLoginPassword("");
      setIsVerifyingOtp(false);
      setIsLoading(false);
      
      await refreshUser();
      navigate('/', { replace: true });

    } catch (error: any) {
      setOtpError("Şifre sıfırlama sırasında beklenmeyen bir hata oluştu.");
      setIsVerifyingOtp(false);
      setIsLoading(false);
    }
  };

  // Registration OTP handlers
  const handleRegisterOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterOtpError("");

    if (!validateOtp(registerOtpCode)) {
      setRegisterOtpError("Lütfen 6 haneli doğrulama kodunu giriniz.");
      return;
    }

    if (!registerOtpEmail) {
      setRegisterOtpError("Doğrulama sırasında bir hata oluştu. E-posta bulunamadı.");
      return;
    }

    setIsVerifyingRegisterOtp(true);
    setIsLoading(true);

    try {
      const verifyResponse = await authAPI.verifyOtp({
        email: registerOtpEmail,
        token: registerOtpCode,
        type: 'signup'
      });

      if (!verifyResponse || verifyResponse.error) {
        if (verifyResponse?.error) {
          setRegisterOtpError(verifyResponse.error.message || "Geçersiz veya süresi dolmuş doğrulama kodu.");
        } else {
          setRegisterOtpError("Doğrulama başarılı ancak oturum başlatılamadı.");
        }
        setIsVerifyingRegisterOtp(false);
        setIsLoading(false);
        return;
      }

      // After successful OTP verification, user is authenticated - set up session like login
      if (verifyResponse && verifyResponse.data && verifyResponse.data.session) {
        const canEnter = await checkVersionGate();
        if (!canEnter) {
          await authAPI.signOut();
          setRegisterOtpError("Uygulamayi kullanmaya devam etmek icin guncelleme yapmaniz gerekiyor.");
          setIsVerifyingRegisterOtp(false);
          setIsLoading(false);
          return;
        }

        // Set session expiry
        const expiryTime = Math.floor(Date.now() / 1000) + 86400; // 24 hours
        localStorage.setItem('santiyex-session-expiry', expiryTime.toString());
        
        // Update OneSignal in background
        if (isNativePlatform()) {
          setTimeout(async () => {
            try {
              await syncNotificationState(verifyResponse.data.session.user.id);
              const { updateMissingOneSignalIds } = await import('@/utils/onesignal-migration');
              await updateMissingOneSignalIds();
            } catch (syncError) {
              console.error('OneSignal sync error after registration:', syncError);
            }
          }, 1500);
        }
        
        // Refresh user data to establish session
        const userRefreshSuccess = await refreshUser();
        
        if (userRefreshSuccess) {
          // Set up subscription refresh flags (same as login)
          localStorage.setItem('santiyex-force-subscription-refresh', 'true');
          
          // Trigger subscription refresh after a short delay
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('santiyex-force-subscription-refresh'));
          }, 500);
          
          toast({
            title: "Hesap Doğrulandı!",
            description: "Hesabınız başarıyla doğrulandı. Ana sayfaya yönlendiriliyorsunuz.",
            variant: "default",
          });

          // Reset OTP state
          setIsEnteringRegisterOtp(false);
          setRegisterOtpEmail("");
          setRegisterOtpCode("");
          setRegisterOtpError("");
          setIsVerifyingRegisterOtp(false);
          setIsLoading(false);
          
          // Navigation will be handled by AuthContext useEffect when isAuthenticated becomes true
        } else {
          setRegisterOtpError("Oturum başlatılamadı. Lütfen giriş sayfasından manuel olarak giriş yapın.");
          cancelRegisterOtpFlow();
        }
      } else {
        toast({
          title: "Hesap Doğrulandı!",
          description: "Hesabınız başarıyla doğrulandı. Lütfen giriş sayfasından giriş yapın.",
          variant: "default",
        });
        cancelRegisterOtpFlow();
      }

    } catch (error: any) {
      setRegisterOtpError("Doğrulama sırasında beklenmeyen bir hata oluştu.");
      setIsVerifyingRegisterOtp(false);
      setIsLoading(false);
    }
  };

  const cancelRegisterOtpFlow = () => {
    setIsEnteringRegisterOtp(false);
    setRegisterOtpEmail("");
    setRegisterOtpCode("");
    setRegisterOtpError("");
    setIsVerifyingRegisterOtp(false);
    setActiveTab("login");
  };

  const cancelOtpFlow = () => {
    setIsEnteringOtp(false);
    setResetEmail("");
    setOtpCode("");
    setNewPassword("");
    setConfirmNewPassword("");
    setOtpError("");
    setIsVerifyingOtp(false);
  };

  const authInputBaseClass =
    "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-md focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:border-brand-600";
  const authPrimaryButtonClass =
    "w-full h-11 rounded-md bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white font-semibold shadow-sm transition-colors";

  if (updateRequired) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-25 p-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-7 shadow-sm">
          <div className="mb-4 flex items-center gap-2 text-error-600">
            <AlertCircle className="h-5 w-5" />
            <h2 className="text-lg font-semibold">Update Required</h2>
          </div>
          <p className="text-sm text-gray-700">
            Your app version ({deviceAppVersion || "unknown"}) is older than the required version ({requiredAppVersion || "unknown"}).
          </p>
          <p className="mt-2 text-sm text-gray-700">
            Please update the app before entering.
          </p>
          {updateLink ? (
            <a
              href={updateLink}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex h-11 w-full items-center justify-center rounded-md bg-brand-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
            >
              Update App
            </a>
          ) : (
            <p className="mt-4 text-sm text-error-600">
              Update link is not configured.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="login-register-container min-h-screen bg-gray-25 relative overflow-auto"
      style={{
        overflow: 'auto',
        overflowY: 'auto',
        overflowX: 'hidden',
        height: 'auto',
        minHeight: '100vh',
        position: 'relative',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Main container */}
      <div className="flex items-center justify-center min-h-screen p-4 py-8">
        <div className="relative z-10 w-full max-w-md mx-auto">
          <div className="relative bg-white rounded-2xl border border-gray-200 shadow-sm p-7">
            {/* Language Selection Button */}
            <div className="absolute top-4 right-4 z-20">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsLanguageModalOpen(true)}
                className="flex items-center space-x-1 text-xs rounded-md border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 font-medium shadow-xs"
              >
                <Languages className="h-3 w-3" />
                <span>{getCurrentLanguageLabel()}</span>
              </Button>
            </div>

            {/* Header Section */}
            <div className="text-center mb-8 relative z-10">
              <div className="relative mx-auto mb-4 w-16 h-16">
                <div className="relative flex items-center justify-center w-full h-full bg-brand-600 rounded-xl shadow-sm">
                  <Building className="h-8 w-8 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t('app.name')}</h1>
              <p className="text-sm text-gray-600 mt-2">{t('auth.description')}</p>
            </div>

            {/* Content container with enhanced scrolling */}
            <div className="relative z-10 max-h-[80vh] overflow-y-auto scrollbar-hide touch-pan-y" style={{ WebkitOverflowScrolling: 'touch' }}>
              
              {/* Conditionally render OTP forms or main tabs */}
              {isEnteringOtp ? (
                /* Password Reset OTP Form */
                <div className="space-y-6">
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900">{t('auth.reset_password')}</h2>
                    <p className="text-sm text-gray-600 mt-2 font-medium">
                      {t('auth.otp_sent_to_email', { email: resetEmail })}
                    </p>
                  </div>
                  <form onSubmit={handleOtpSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <label htmlFor="otp-code" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('auth.verification_code')}</label>
                      <div className="relative">
                        <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <Input
                          id="otp-code"
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="one-time-code"
                          maxLength={6}
                          placeholder={t('auth.six_digit_code')}
                          className={`pl-10 tracking-widest ${authInputBaseClass}`}
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="new-password" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('auth.new_password')}</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <Input
                          id="new-password"
                          type={showNewPassword ? "text" : "password"}
                          placeholder={t('auth.enter_new_password')}
                          className={`pl-10 pr-10 ${authInputBaseClass}`}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="confirm-new-password" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('auth.confirm_password')}</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <Input
                          id="confirm-new-password"
                          type={showConfirmNewPassword ? "text" : "password"}
                          placeholder={t('auth.reenter_new_password')}
                          className={`pl-10 pr-10 ${authInputBaseClass}`}
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                        >
                          {showConfirmNewPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {otpError && (
                      <div className="flex items-center space-x-2 text-error-600 bg-error-50 p-3 rounded-md border border-error-100">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <span className="text-sm font-semibold">{otpError}</span>
                      </div>
                    )}

                    <div className="flex space-x-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={cancelOtpFlow}
                        disabled={isVerifyingOtp}
                        className="flex-1 bg-white border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={isVerifyingOtp || !otpCode || !newPassword || !confirmNewPassword}
                        className={`flex-1 ${authPrimaryButtonClass}`}
                      >
                        {isVerifyingOtp ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          t('auth.update_password')
                        )}
                      </Button>
                    </div>
            </form>
          </div>
              ) : isEnteringRegisterOtp ? (
                /* Registration OTP Form */
                <div className="space-y-6">
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('auth.account_verification')}</h2>
                    <p className="text-sm text-gray-600 font-medium">
                      {t('auth.verification_code_sent_to', { email: registerOtpEmail })}
                    </p>
                  </div>
                  <form onSubmit={handleRegisterOtpSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <label htmlFor="register-otp-code" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('auth.verification_code')}</label>
                      <div className="relative">
                        <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <Input
                          id="register-otp-code"
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="one-time-code"
                          maxLength={6}
                          placeholder={t('auth.six_digit_code')}
                          className={`pl-10 tracking-widest ${authInputBaseClass}`}
                          value={registerOtpCode}
                          onChange={(e) => setRegisterOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                          required
                        />
                      </div>
                    </div>

                    {registerOtpError && (
                      <div className="flex items-center space-x-2 text-error-600 bg-error-50 p-3 rounded-md border border-error-100">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <span className="text-sm font-semibold">{registerOtpError}</span>
                      </div>
                    )}

                    <div className="flex space-x-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={cancelRegisterOtpFlow}
                        disabled={isVerifyingRegisterOtp}
                        className="flex-1 bg-white border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={isVerifyingRegisterOtp || !registerOtpCode}
                        className={`flex-1 ${authPrimaryButtonClass}`}
                      >
                        {isVerifyingRegisterOtp ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          t('auth.verify')
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              ) : (
                /* Main Login/Register Tabs */
                <Tabs
                  defaultValue="login"
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="w-full"
                >
                  <TabsList className="grid grid-cols-2 w-full mb-6 bg-gray-100 border border-gray-200 rounded-md p-1 shadow-xs">
                    <TabsTrigger
                      value="login"
                      className="py-2.5 px-4 font-semibold transition-all duration-200 rounded-md text-sm border-0 text-gray-600 hover:bg-gray-50 data-[state=active]:bg-brand-600 data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=active]:border-0"
                    >
                      {t('auth.login')}
                    </TabsTrigger>
                    <TabsTrigger
                      value="register"
                      className="py-2.5 px-4 font-semibold transition-all duration-200 rounded-md text-sm border-0 text-gray-600 hover:bg-gray-50 data-[state=active]:bg-brand-600 data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=active]:border-0"
                    >
                      {t('auth.register')}
                    </TabsTrigger>
                  </TabsList>
            
                  <TabsContent value="login">
                    <div className="space-y-6">
                      <form onSubmit={handleLogin} className="space-y-5">
                        <div className="space-y-2">
                          <label htmlFor="email" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('auth.email')}</label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <Input
                              id="email"
                              type="email"
                              placeholder={t('auth.enter_email')}
                              className={`pl-10 ${authInputBaseClass}`}
                              value={loginEmail}
                              onChange={(e) => {
                                setLoginEmail(e.target.value);
                                setLoginError("");
                              }}
                              required
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <label htmlFor="password" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('auth.password')}</label>
                            <Button
                              type="button"
                              variant="link"
                              onClick={handleForgotPassword}
                              className="text-brand-600 text-xs h-auto p-0 hover:underline disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                              disabled={isLoading || isSendingOtp || !loginEmail || !validateEmail(loginEmail)}
                            >
                              {isSendingOtp ? t('auth.sending') : t('auth.forgot_password')}
                            </Button>
                          </div>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <Input
                              id="password"
                              type={showPassword ? "text" : "password"}
                              placeholder={t('auth.enter_password')}
                              className={`pl-10 pr-10 ${authInputBaseClass}`}
                              value={loginPassword}
                              onChange={(e) => {
                                setLoginPassword(e.target.value);
                                setLoginError("");
                              }}
                              required
                            />
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                              onClick={() => setShowPassword(!showPassword)}
                              aria-label={showPassword ? t('auth.hide_password') : t('auth.show_password')}
                            >
                              {showPassword ? (
                                <EyeOff className="h-5 w-5" />
                              ) : (
                                <Eye className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                        </div>
                        
                        {(loginError || otpError) && (
                          <div className="flex items-center space-x-2 text-error-600 bg-error-50 p-3 rounded-md border border-error-100">
                            <AlertCircle className="h-4 w-4 shrink-0" />
                            <span className="text-sm font-semibold">{loginError || otpError}</span>
                          </div>
                        )}
                        
                        <Button 
                          type="submit" 
                          disabled={isLoading || isSendingOtp || !loginEmail || !loginPassword}
                          className={`w-full flex items-center justify-center ${authPrimaryButtonClass}`}
                        >
                          {isLoading ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <span>{t('auth.login')}</span>
                          )}
                        </Button>
                      </form>
                    </div>
                  </TabsContent>
            
                  <TabsContent value="register">
                    <div className="space-y-6">
                      <div className="max-h-[60vh] overflow-y-auto scrollbar-hide pr-2" style={{ WebkitOverflowScrolling: 'touch' }}>
                        <form onSubmit={handleRegister} className="space-y-4">
                          {/* Full Name */}
                          <div className="space-y-2">
                            <label htmlFor="full-name" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('auth.full_name')} *</label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                              <Input
                                id="full-name"
                                type="text"
                                placeholder={t('auth.enter_full_name')}
                                className={`pl-10 ${authInputBaseClass} ${fieldErrors.fullName ? 'border-error-600 focus-visible:border-error-600' : ''}`}
                                value={fullName}
                                onChange={(e) => {
                                  setFullName(e.target.value);
                                  validateField('fullName', e.target.value);
                                }}
                                onBlur={(e) => validateField('fullName', e.target.value)}
                                required
                              />
                            </div>
                            {fieldErrors.fullName && (
                              <div className="flex items-center space-x-1 text-error-600 text-xs">
                                <AlertCircle className="h-3 w-3" />
                                <span className="font-medium">{fieldErrors.fullName}</span>
                              </div>
                            )}
                          </div>

                          {/* Email */}
                          <div className="space-y-2">
                            <label htmlFor="register-email" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('auth.email')} *</label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                              <Input
                                id="register-email"
                                type="email"
                                placeholder={t('auth.enter_email')}
                                className={`pl-10 ${authInputBaseClass} ${fieldErrors.email ? 'border-error-600 focus-visible:border-error-600' : ''}`}
                                value={registerEmail}
                                onChange={(e) => {
                                  setRegisterEmail(e.target.value);
                                  validateField('email', e.target.value);
                                }}
                                onBlur={(e) => validateField('email', e.target.value)}
                                required
                              />
                            </div>
                            {fieldErrors.email && (
                              <div className="flex items-center space-x-1 text-error-600 text-xs">
                                <AlertCircle className="h-3 w-3" />
                                <span className="font-medium">{fieldErrors.email}</span>
                              </div>
                            )}
                          </div>

                          {/* Password */}
                          <div className="space-y-2">
                            <label htmlFor="register-password" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('auth.password')} *</label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                              <Input
                                id="register-password"
                                type={showPassword ? "text" : "password"}
                                placeholder={t('auth.enter_password')}
                                className={`pl-10 pr-10 ${authInputBaseClass} ${fieldErrors.password ? 'border-error-600 focus-visible:border-error-600' : ''}`}
                                value={registerPassword}
                                onChange={(e) => {
                                  setRegisterPassword(e.target.value);
                                  validateField('password', e.target.value);
                                  if (confirmPassword) {
                                    validateField('confirmPassword', confirmPassword);
                                  }
                                }}
                                onBlur={(e) => validateField('password', e.target.value)}
                                required
                              />
                              <button
                                type="button"
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? t('auth.hide_password') : t('auth.show_password')}
                              >
                                {showPassword ? (
                                  <EyeOff className="h-5 w-5" />
                                ) : (
                                  <Eye className="h-5 w-5" />
                                )}
                              </button>
                            </div>
                            {fieldErrors.password && (
                              <div className="flex items-center space-x-1 text-error-600 text-xs">
                                <AlertCircle className="h-3 w-3" />
                                <span className="font-medium">{fieldErrors.password}</span>
                              </div>
                            )}
                          </div>

                          {/* Confirm Password */}
                          <div className="space-y-2">
                            <label htmlFor="confirm-password" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('auth.confirm_password')} *</label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                              <Input
                                id="confirm-password"
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder={t('auth.confirm_password')}
                                className={`pl-10 pr-10 ${authInputBaseClass} ${fieldErrors.confirmPassword ? 'border-error-600 focus-visible:border-error-600' : ''}`}
                                value={confirmPassword}
                                onChange={(e) => {
                                  setConfirmPassword(e.target.value);
                                  validateField('confirmPassword', e.target.value);
                                }}
                                onBlur={(e) => validateField('confirmPassword', e.target.value)}
                                required
                              />
                              <button
                                type="button"
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                aria-label={showConfirmPassword ? t('auth.hide_password') : t('auth.show_password')}
                              >
                                {showConfirmPassword ? (
                                  <EyeOff className="h-5 w-5" />
                                ) : (
                                  <Eye className="h-5 w-5" />
                                )}
                              </button>
                            </div>
                            {fieldErrors.confirmPassword && (
                              <div className="flex items-center space-x-1 text-error-600 text-xs">
                                <AlertCircle className="h-3 w-3" />
                                <span className="font-medium">{fieldErrors.confirmPassword}</span>
                              </div>
                            )}
                          </div>

                          {/* Company Name */}
                          <div className="space-y-2">
                            <label htmlFor="company" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('auth.company_name_optional')}</label>
                            <div className="relative">
                              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                              <Input
                                id="company"
                                type="text"
                                placeholder={t('auth.enter_company_name')}
                                className={`pl-10 ${authInputBaseClass}`}
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                              />
                            </div>
                          </div>

                          {/* Location Selector */}
                          <div className="space-y-2">
                            <label className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600 flex items-center">
                              <MapPin size={14} className="mr-1.5 text-gray-400" /> {t('auth.location')} *
                            </label>
                            <div className="bg-white border border-gray-200 rounded-md shadow-xs">
                              <LocationSelector
                                value={location}
                                onChange={(newLocation) => {
                                  setLocation(newLocation);
                                  validateField('location', newLocation);
                                }}
                              />
                            </div>
                            {fieldErrors.location && (
                              <div className="flex items-center space-x-1 text-error-600 text-xs">
                                <AlertCircle className="h-3 w-3" />
                                <span className="font-medium">{fieldErrors.location}</span>
                              </div>
                            )}
                          </div>

                          {/* Role Selector */}
                          <div className="space-y-2">
                            <label htmlFor="role-select" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600 flex items-center">
                              <Star size={14} className="mr-1.5 text-gray-400" /> {t('auth.your_role')} *
                            </label>
                            <Select
                              value={selectedRole}
                              onValueChange={(value: PersonCategory) => {
                                setSelectedRole(value);
                                validateField('role', value);
                              }}
                              required
                            >
                              <SelectTrigger
                                id="role-select"
                                className={`w-full ${authInputBaseClass} ${fieldErrors.role ? 'border-error-600 focus-visible:border-error-600' : ''}`}
                              >
                                <SelectValue placeholder={t('auth.select_role')} className="text-gray-400 font-medium" />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200 rounded-md">
                                {CATEGORY_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value} className="focus:bg-brand-50 text-gray-600 font-medium">
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {fieldErrors.role && (
                              <div className="flex items-center space-x-1 text-error-600 text-xs">
                                <AlertCircle className="h-3 w-3" />
                                <span className="font-medium">{fieldErrors.role}</span>
                              </div>
                            )}
                          </div>

                          {/* Register Error */}
                          {registerError && (
                            <div className="flex items-center space-x-2 text-error-600 bg-error-50 p-3 rounded-md border border-error-100">
                              <AlertCircle className="h-4 w-4 shrink-0" />
                              <span className="text-sm font-semibold">{registerError}</span>
                            </div>
                          )}

                          <Button 
                            type="submit" 
                            disabled={isLoading || !fullName || !registerEmail || !registerPassword || !confirmPassword || !location.country || !location.city || !selectedRole}
                            className={`w-full flex items-center justify-center ${authPrimaryButtonClass}`}
                          >
                            {isLoading ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <span>{t('auth.register')}</span>
                            )}
                          </Button>
                        </form>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Language Selection Modal */}
      <Modal
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
        title={t('settings.language_selection')}
      >
        <div className="p-4 bg-white rounded-xl">
          <LanguageSelector
            showIcon={false}
            showLabel={false}
            className="w-full"
          />
        </div>
      </Modal>
    </div>
  );
};

export default LoginPage;
